import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { BillingAccountService, GetBillingAccountResponseSubscriptionDto, SubscriptionsService } from 'app/API';
import { useEffect, useState } from 'react';

import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './styles/Content';
import { RangeSliderWrapper } from './styles/RangeSliderWrapper';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  subscription: GetBillingAccountResponseSubscriptionDto;
};

export const EditASOneSubscriptionModal = ({ onCancel, onSuccess, subscription }: TProps) => {
  const { call } = useAPI();
  const { openModal } = useModalContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [price, setPrice] = useState(0);
  const assignedCount = subscription?.licenses.filter((lic) => lic.assigneeId !== null).length;
  const totalCount = subscription?.licenses.length;
  const minLicensesCount = assignedCount > 1 ? assignedCount : 1;
  const [numberOfLicenses, setNumberOfLicenses] = useState(totalCount);
  const [freeSeats, setFreeSeats] = useState(0);

  useEffect(() => {
    (async () => {
      const plans = await call(BillingAccountService.getAllAsOnePlans());
      const plan = plans.find((item) => item.id === subscription.productId);
      setPrice(Number(plan?.price ?? 0));
      if (plan) {
        setFreeSeats(plan?.freeSeats);
      }
    })();
  }, []);

  const innerSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSubmitting(true);
    try {
      const result = await call(
        SubscriptionsService.update({
          baId: subscription.billingAccountId,
          subscriptionId: subscription.id,
          requestBody: { licenseCount: numberOfLicenses },
        }),
      );
      if (!result || !result.success) {
        let message = 'Something went wrong. Please contact administrator.';
        if (result.error === 'No payment methods found') {
          message = 'Cannot create subscription. Please add the payment method first';
        }
        if (result.error === 'Subscription already exists') {
          message = 'You already have this subscription. Please choose another or modify an existing subscription';
        }
        if (result.error === 'Subscription has a failed payment') {
          message = 'This subscription has a failed payment. Please resolve your billing before making changes.';
        }
        onCancel();
        openModal(ModalNamesEnum.GenericErrorModal, {
          message,
        });
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
      onSuccess();
    } catch {
      onCancel();
      openModal(ModalNamesEnum.GenericErrorModal, {
        message: 'Something went wrong. Please contact administrator.',
      });
    }
  };

  return (
    <ModalWrapper width="480px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle data-testid="purchasePlan">Confirm</ModalTitle>
      <Content>
        <p className="description">You are about to change the number of licenses for this subscription</p>

        <RangeSliderWrapper>
          <label htmlFor="steps-range" className="block mb-2 font-[600] text-xs text-foreground-muted text-left">
            Number of licenses:
          </label>
          <input
            id="steps-range"
            type="range"
            min={minLicensesCount}
            max={250}
            value={numberOfLicenses}
            step={1}
            onChange={(e) => setNumberOfLicenses(Number(e.target.value))}
            className="w-full h-2 bg-[var(--color-surface-muted)] rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs">
            {numberOfLicenses} license{numberOfLicenses > 1 && 's'}
          </span>
        </RangeSliderWrapper>

        <div className="text-left my-4 flex flex-col">
          {freeSeats ? (
            <div>
              <span className="font-[600] text-xs text-foreground-muted">Free licenses in subscription plan:</span>
              <span className="float-right">{freeSeats}</span>
            </div>
          ) : null}
          <div>
            <span className="font-[600] text-xs text-foreground-muted">Total subscription price per month:</span>
            <span className="float-right">
              ${numberOfLicenses <= freeSeats ? 0 : (Number(price) * (numberOfLicenses - freeSeats)).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="buttonContainer">
          <OutlinedButton type="button" onClick={onCancel}>
            Cancel
          </OutlinedButton>
          <Button data-testid="submitButton" type="submit" onClick={innerSubmit} disabled={isSubmitting}>
            Update
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
